import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import axios from 'axios';
import { configurationApi } from '../api/configuration.api';
import { useConfigurationStore } from '../store/configuration.store';
import { PeriodeSelector } from '../../bulletins/components/PeriodeSelector';

export function ConfigurationDashboardPage() {
  const { periodeConsultee, setPeriode } = useConfigurationStore();
  const { mois, annee } = periodeConsultee;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['configuration', { mois, annee }],
    queryFn: () => configurationApi.obtenirPeriode(annee, mois),
  });

  const { data: org } = useQuery({
    queryKey: ['organisation-moi'],
    queryFn: () => axios.get('/api/v1/paie/administration/organisation/moi').then((r) => r.data?.data ?? null),
  });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-content">Configuration de paie</h1>
        <PeriodeSelector mois={mois} annee={annee} onChange={setPeriode} />
      </div>

      {isLoading && <p className="text-content-subtle text-sm">Chargement...</p>}

      {isError && (
        <div className="bg-warning-light border border-warning rounded p-4 text-warning text-sm">
          ⚠️ Aucune configuration valide pour {mois}/{annee}. Veuillez créer les paramètres.
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ConfigCard titre="CNSS Salariale" valeur={`${(data.cnss?.tauxEmploye * 100).toFixed(1)}%`} sous={`Plafond : ${data.cnss?.plafondMensuel?.toLocaleString('fr-FR')} F`} />
          <ConfigCard titre="CNSS Patronale" valeur={`${(data.cnss?.tauxPatronal * 100).toFixed(1)}%`} sous="Part employeur" />
          <ConfigCard titre="VPS" valeur={`${(data.vps?.taux * 100).toFixed(1)}%`} sous="Versement Patronal Supplémentaire" />
          <ConfigCard titre="Barème ITS" valeur={`${data.itsConfig?.tranches?.length ?? 0} tranche(s)`} sous="Impôt progressif" />
        </div>
      )}

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ['/paie/configuration/parametres', 'Paramètres'],
          ['/paie/configuration/baremes', 'Barèmes ITS'],
          ['/paie/configuration/simulateur', 'Simulateur'],
          ['/paie/configuration/theme', 'Thème & couleurs'],
        ].map(([to, label]) => (
          <Link key={to} to={to} className="p-4 bg-surface border border-border rounded-lg hover:border-primary hover:shadow-sm text-sm font-medium text-content text-center">
            {label}
          </Link>
        ))}
      </div>

      <div className="mt-4">
        <Link
          to="/paie/configuration/organisation"
          className="flex items-center gap-3 p-4 bg-surface border border-border rounded-lg hover:border-primary hover:shadow-sm transition-colors w-full md:w-auto"
        >
          <Building2 className="w-5 h-5 text-primary-text shrink-0" />
          <div className="text-left">
            <p className="text-sm font-medium text-content">Organisation</p>
            <p className="text-xs text-content-subtle">
              {org ? `${org.typeOrganisation} — ${org.referentielComptable}` : 'Non configuré'}
            </p>
          </div>
        </Link>
      </div>
    </div>
  );
}

function ConfigCard({ titre, valeur, sous }: { titre: string; valeur: string; sous: string }) {
  return (
    <div className="bg-surface border border-border rounded-lg p-4">
      <p className="text-xs text-content-subtle uppercase font-medium mb-1">{titre}</p>
      <p className="text-2xl font-bold text-content">{valeur}</p>
      <p className="text-xs text-content-subtle mt-1">{sous}</p>
    </div>
  );
}
